// Layout — shell de páginas com sidebar nav fixa.
// Aplicado em rotas "navegáveis" (Forms / Apps / Coleções / Integrações).
// Páginas full-screen (Builder, Responses, AppDetail, CollectionDetail)
// NÃO usam esse Layout — renderizam diretamente.

import { NavLink, Outlet, useNavigate, useLocation, useMatch, Link } from 'react-router-dom';
import { Layers, FileText, Boxes, Database, PlugZap, LogOut, Settings, Home as HomeIcon, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect } from 'react';
import AppSettingsModal from './dashboard/AppSettingsModal';
import { loadAppSettings } from '../utils/appSettings';
import { dialog } from './Dialog';
import { customersApi, type CustomerDTO } from '../lib/api';

// 2026-05-18: items expandidos com Workspace (home) + Forms (rota dedicada /forms).
// Workspace é a rota /; /forms substitui /dashboard. /dashboard fica como redirect.
const ITEMS = [
  { to: '/',              label: 'Workspace',    Icon: HomeIcon, end: true },
  { to: '/forms',         label: 'Forms',        Icon: FileText, end: false },
  { to: '/apps',          label: 'Apps',         Icon: Boxes,    end: false },
  { to: '/collections',   label: 'Coleções',     Icon: Database, end: false },
  { to: '/integrations',  label: 'Integrações',  Icon: PlugZap,  end: false },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSettings, setShowSettings] = useState(false);
  const [appLogo, setAppLogo] = useState('');
  const [customerCtx, setCustomerCtx] = useState<CustomerDTO | null>(null);

  useEffect(() => { setAppLogo(loadAppSettings().logo); }, []);

  // 2026-05-18: detecta contexto de cliente via URL (/customers/:slug/...).
  // Carrega o customer pra mostrar no top da sidebar com avatar+cor.
  const customerMatch = useMatch('/customers/:slug/*');
  const customerSlug = customerMatch?.params.slug ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!customerSlug) { setCustomerCtx(null); return; }
    customersApi.getBySlug(customerSlug)
      .then((r) => { if (!cancelled) setCustomerCtx(r.customer); })
      .catch(() => { if (!cancelled) setCustomerCtx(null); });
    return () => { cancelled = true; };
  }, [customerSlug]);

  async function handleLogout() {
    const ok = await dialog.confirm('Sair da conta?', { confirmLabel: 'Sair' });
    if (!ok) return;
    await logout();
    navigate('/');
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--app-bg, #f1f5f9)' }}>
      {/* Sidebar */}
      <aside
        className="sticky top-0 h-screen w-56 shrink-0 border-r flex flex-col"
        style={{ background: 'var(--app-sidebar, #ffffff)', borderColor: 'var(--app-border, #e5e7eb)' }}
      >
        {/* Logo */}
        <div className="px-4 py-4 flex items-center gap-2 border-b" style={{ borderColor: 'var(--app-border, #e5e7eb)' }}>
          {appLogo ? (
            <img src={appLogo} alt="Logo" className="h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--app-accent, #6366f1)' }}>
              <Layers size={16} className="text-white" />
            </div>
          )}
          <div className="leading-tight">
            <div className="text-sm font-bold" style={{ color: 'var(--app-text, #1f2937)' }}>NanoApp</div>
            <div className="text-[10px]" style={{ color: 'var(--app-text-muted, #6b7280)' }}>Builder</div>
          </div>
        </div>

        {/* 2026-05-18: Customer context card — só aparece quando user está em /customers/:slug/... */}
        {customerCtx && (
          <div className="mx-2 mt-3 mb-1 p-2.5 rounded-lg border" style={{
            background: (customerCtx.color || '#6366f1') + '12',
            borderColor: (customerCtx.color || '#6366f1') + '40',
            borderLeftWidth: '4px',
            borderLeftColor: customerCtx.color || '#6366f1',
          }}>
            <div className="flex items-center justify-between gap-1 mb-1">
              <Link
                to="/"
                className="text-[10px] text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1"
                title="Voltar ao Workspace"
              >
                <ArrowLeft size={10} /> Workspace
              </Link>
            </div>
            <Link to={`/customers/${customerCtx.slug}`} className="flex items-center gap-1.5 hover:opacity-80">
              <span className="text-lg shrink-0">{customerCtx.icon || '👤'}</span>
              <span className="text-xs font-semibold truncate" style={{ color: 'var(--app-text, #1f2937)' }}>
                {customerCtx.name}
              </span>
            </Link>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {ITEMS.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User + actions */}
        <div className="px-2 py-3 border-t" style={{ borderColor: 'var(--app-border, #e5e7eb)' }}>
          <div className="px-3 py-1.5 mb-1">
            <div className="text-[11px] uppercase tracking-wide font-medium" style={{ color: 'var(--app-text-muted, #94a3b8)' }}>
              Logado como
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--app-text, #1f2937)' }} title={user?.email ?? ''}>
              {user?.email ?? ''}
            </div>
          </div>
          <button
            onClick={() => { setShowSettings(true); setAppLogo(loadAppSettings().logo); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Settings size={15} /> Configurações
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <LogOut size={15} /> Sair
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      {showSettings && (
        <AppSettingsModal onClose={() => { setShowSettings(false); setAppLogo(loadAppSettings().logo); }} />
      )}
    </div>
  );
}
